import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import useEventStore, {
    EventModel,
    deleteStorageFileByDownloadUrl,
    calculateNextOccurrence,
    parseLocalEventDate,
    shouldShowMissingTopicReminder,
    getDaysUntilEventDate,
    getEventActiveDateRaw,
    toEventDateISO,
} from '../store/eventStore';
import { db, storage } from '../config/firebase';
import { collection, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
    slugifyEventTitle,
    publicEventPreviewUrl,
    publicEventOpenUrl,
    syncPublicOnlineEvent,
} from '../utils/publicOnlineEventSync';
import { clarifyOnlineFormat } from '../utils/onlineEventWording';
import useAdminStore from '../store/adminStore';
import { loadMailRecipientNames } from '../utils/mailGreeting';

/** Kern-Referenten für Checkboxen (Eventansicht + öffentliche Landing). */
const CORE_EVENT_SPEAKERS: { name: string; role: string }[] = [
    { name: 'Christian Funk', role: 'Friseurmeister & Gründer PreisExpert' },
    { name: 'Martina Jovanovic', role: 'PreisExpert' },
    { name: 'Jean Standardt', role: 'PreisExpert' },
    { name: 'Nelly Rühle', role: 'PreisExpert' },
];

/** Alte gespeicherte Namen → aktuelle Anzeigenamen (Checkbox bleibt gesetzt). */
const EVENT_SPEAKER_NAME_ALIASES: Record<string, string> = {
    'Martina Funk': 'Martina Jovanovic',
    Jean: 'Jean Standardt',
    'Jean Standard': 'Jean Standardt',
    Nelly: 'Nelly Rühle',
};

const normalizeEventSpeakerName = (name: string): string => {
    const trimmed = String(name || '').trim();
    return EVENT_SPEAKER_NAME_ALIASES[trimmed] || trimmed;
};

const CORE_SPEAKER_NAMES = CORE_EVENT_SPEAKERS.map((s) => s.name);

const isQuillDescriptionEmpty = (html: string): boolean =>
    !String(html || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .trim();

const IconBase = ({ children, size = 20, className = "" }: any) => <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{children}</svg>;
const XIcon = (props: any) => <IconBase {...props}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></IconBase>;
const CalendarIcon = (props: any) => <IconBase {...props}><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></IconBase>;
const PlusIcon = (props: any) => <IconBase {...props}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></IconBase>;
const TrashIcon = (props: any) => <IconBase {...props}><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></IconBase>;
const ArchiveIcon = (props: any) => <IconBase {...props}><rect width="20" height="5" x="2" y="3" rx="1"/><path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"/><path d="M10 12h4"/></IconBase>;
const EditIcon = (props: any) => <IconBase {...props}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></IconBase>;
const ClockIcon = (props: any) => <IconBase {...props}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></IconBase>;
const ImageIcon = (props: any) => <IconBase {...props}><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></IconBase>;
const UploadCloudIcon = (props: any) => <IconBase {...props}><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/><polyline points="16 16 12 12 8 16"/></IconBase>;
const AlertTriangleIcon = (props: any) => <IconBase {...props}><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></IconBase>;
const ExternalLinkIcon = (props: any) => <IconBase {...props}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></IconBase>;
const FlaskIcon = (props: any) => <IconBase {...props}><path d="M9 3H15M10 3V14L5.5 19.5A2 2 0 0 0 7 23H17A2 2 0 0 0 18.5 19.5L14 14V3M10 14H14"/></IconBase>; 
const MailIcon = (props: any) => <IconBase {...props}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></IconBase>;

const CopyIcon = (props: any) => <IconBase {...props}><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></IconBase>;
const CheckIcon = (props: any) => <IconBase {...props}><polyline points="20 6 9 17 4 12"/></IconBase>;
const UsersIcon = (props: any) => <IconBase {...props}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></IconBase>;
const ChevronIcon = (props: any) => <IconBase {...props}><polyline points="6 9 12 15 18 9"/></IconBase>;

type LandingSignup = { id: string; email: string; firstName: string; createdAt: number };

/** Newsletter-analog: zwei unabhängige Haupt-Zielgruppen */
const EVENT_AUDIENCE_OPTIONS: { key: 'mandanten' | 'extern'; label: string; hint: string }[] = [
    { key: 'mandanten', label: 'Mandanten / Intern', hint: 'Bestehende Kunden / System-Nutzer' },
    { key: 'extern', label: 'Externe', hint: 'Externe Interessenten / Landingpage-Leads' },
];

/** Legacy → kanonisch: nur 'mandanten' | 'extern' (wie Newsletter targetClients / targetExternal) */
const normalizeAudienceKeysForForm = (raw: unknown): Array<'mandanten' | 'extern'> => {
    const list = Array.isArray(raw)
        ? raw.filter((e): e is string => typeof e === 'string' && e.trim().length > 0)
        : typeof raw === 'string' && raw.trim()
          ? [raw.trim()]
          : [];
    const out = new Set<'mandanten' | 'extern'>();
    for (const entry of list) {
        const k = entry.trim().toLowerCase();
        if (k === 'mandanten' || k === 'basic' || k === 'clients' || k === 'client' || k === 'intern' || k === 'interne' || k === 'insider' || k === 'vip' || k === 'team') {
            out.add('mandanten');
        } else if (k === 'extern' || k === 'externe' || k === 'external' || k === 'leads') {
            out.add('extern');
        }
    }
    return Array.from(out);
};

interface EventAdminModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const EventAdminModal: React.FC<EventAdminModalProps> = ({ isOpen, onClose }) => {
    const { events, archivedEvents, fetchEvents, addEvent, updateEvent, deleteEvent, archiveEvent, restoreEventFromArchive, clearMonthlyContent, isLoading } = useEventStore();
    const [view, setView] = useState<'list' | 'form'>('list');
    const [listTab, setListTab] = useState<'active' | 'archive'>('active');
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    
    // Status für den echten Massenversand
    const [isMassSending, setIsMassSending] = useState<string | null>(null);

    const [testEmail, setTestEmail] = useState('');
    const [isTestSending, setIsTestSending] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const [slugManual, setSlugManual] = useState(false);
    const [previousPublicSlug, setPreviousPublicSlug] = useState<string>('');

    const [titleImageFile, setTitleImageFile] = useState<File | null>(null);
    const [topicImageFile, setTopicImageFile] = useState<File | null>(null);
    const [guestSpeakerName, setGuestSpeakerName] = useState('');
    const [guestSpeakerRole, setGuestSpeakerRole] = useState('');
    const [signupCounts, setSignupCounts] = useState<Record<string, number>>({});
    const [signupsBySlug, setSignupsBySlug] = useState<Record<string, LandingSignup[]>>({});
    const [signupsOpen, setSignupsOpen] = useState(false);

    // 💉 CHIRURGIE-HILFSFUNKTION (Format-Wächter)
    const ensureISOFormat = (dateVal: any) => {
        if (!dateVal) return '';
        if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateVal)) {
            return dateVal;
        }
        try {
            const d = new Date(dateVal);
            if (!isNaN(d.getTime())) {
                return d.toISOString().split('T')[0];
            }
        } catch (e) {
            console.error("Datum-Formatierungsfehler:", dateVal);
        }
        return String(dateVal); 
    };

    const initialFormState: Omit<EventModel, 'id'> = {
        title: '',
        subtitle: '', 
        eventType: 'Live-Seminar',
        titleImage: '', 
        baseDate: new Date().toISOString().split('T')[0],
        overrideDate: '',
        hasDoubleSession: false,
        timeMorning: '10:00',
        linkMorning: '',
        timeEvening: '19:00',
        linkEvening: '',
        currentTopic: '',
        description: '',
        imageUrl: '',
        targetAudience: ['mandanten'],
        isRecurring: false,
        recurrencePattern: 'fixed',
        isPaidEvent: false,
        registrationUrl: '',
        isPublicOnlineEvent: false,
        publicSlug: '',
        publicTeaser: '',
        publicBullets: [],
        publicSpeakers: [],
        publicCtaLabel: 'Jetzt kostenfreien Platz sichern ❯',
        publicThankYouText: 'Dein Platz ist fest reserviert! Den Zugangslink findest du in der Bestätigungsmail.',
        publicFormat: 'Online-Livestream',
        willBeRecorded: null,
        monthlyWillBeRecorded: null,
    };
    
    const [formData, setFormData] = useState<Omit<EventModel, 'id'>>(initialFormState);

    useEffect(() => {
        if (isOpen) {
            fetchEvents();
            setView('list');
        }
    }, [isOpen, fetchEvents]);

    useEffect(() => {
        if (!isOpen) return;
        const unsubscribe = onSnapshot(
            collection(db, 'online_event_anmeldungen'),
            (snapshot) => {
                const grouped: Record<string, LandingSignup[]> = {};
                snapshot.forEach((docSnap) => {
                    const data = docSnap.data();
                    const slug = String(data.eventSlug || '').trim().toLowerCase();
                    const email = String(data.email || '').trim().toLowerCase();
                    if (!slug || !email.includes('@')) return;
                    const createdRaw = data.createdAt;
                    const createdAt =
                        typeof createdRaw?.toMillis === 'function'
                            ? createdRaw.toMillis()
                            : Date.parse(String(createdRaw || '')) || 0;
                    if (!grouped[slug]) grouped[slug] = [];
                    grouped[slug].push({
                        id: docSnap.id,
                        email,
                        firstName: String(data.firstName || '').trim(),
                        createdAt,
                    });
                });
                const counts: Record<string, number> = {};
                Object.keys(grouped).forEach((slug) => {
                    grouped[slug].sort((a, b) => b.createdAt - a.createdAt);
                    counts[slug] = grouped[slug].length;
                });
                setSignupsBySlug(grouped);
                setSignupCounts(counts);
            },
            (error) => {
                console.warn('EventAdminModal: Anmeldungen konnten nicht geladen werden', error);
            }
        );
        return () => unsubscribe();
    }, [isOpen]);

    const descQuillRef = useRef<ReactQuill>(null);
    const descToolbarUid = useId().replace(/:/g, '');
    const descToolbarTopId = `event-desc-tb-top-${descToolbarUid}`;
    const descToolbarBottomId = `event-desc-tb-bottom-${descToolbarUid}`;

    const descQuillModules = useMemo(
        () => ({
            toolbar: {
                container: `#${descToolbarTopId}`,
            },
        }),
        [descToolbarTopId]
    );

    /** Untere Toolbar spiegelt die obere (gleiche Format-Aktionen). */
    useEffect(() => {
        if (!isOpen || view !== 'form') return;
        const bottom = document.getElementById(descToolbarBottomId);
        const top = document.getElementById(descToolbarTopId);
        if (!bottom || !top) return;

        const forwardClick = (e: Event) => {
            const target = e.target as HTMLElement | null;
            if (!target) return;
            const btn = target.closest('button, .ql-picker') as HTMLElement | null;
            if (!btn || !bottom.contains(btn)) return;
            e.preventDefault();
            e.stopPropagation();
            const cls = Array.from(btn.classList).find((c) => c.startsWith('ql-') && c !== 'ql-active');
            if (!cls) return;
            const value = btn.getAttribute('data-value') || btn.getAttribute('value');
            const topMatch = Array.from(top.querySelectorAll(`.${cls}`)).find((el) => {
                if (!(el instanceof HTMLElement)) return false;
                if (value == null || value === '') return true;
                return (
                    el.getAttribute('data-value') === value ||
                    el.getAttribute('value') === value
                );
            }) as HTMLElement | undefined;
            topMatch?.click();
        };

        bottom.addEventListener('click', forwardClick, true);
        return () => bottom.removeEventListener('click', forwardClick, true);
    }, [isOpen, view, descToolbarTopId, descToolbarBottomId, formData.description]);

    if (!isOpen) return null;

    const handleAudienceToggle = (val: string) => {
        setFormData(prev => {
            const current = prev.targetAudience || [];
            if (current.includes(val)) return { ...prev, targetAudience: current.filter(a => a !== val) };
            return { ...prev, targetAudience: [...current, val] };
        });
    };

    const handleDeleteLandingSignup = async (row: LandingSignup) => {
        const label = row.firstName ? `${row.firstName} (${row.email})` : row.email;
        if (!window.confirm(`Anmeldung von ${label} wirklich löschen?`)) return;
        try {
            await deleteDoc(doc(db, 'online_event_anmeldungen', row.id));
        } catch (error) {
            console.error('Anmeldung konnte nicht gelöscht werden', error);
            window.alert('Löschen fehlgeschlagen. Bitte erneut versuchen.');
        }
    };

    const handleDeleteAllLandingSignups = async (rows: LandingSignup[]) => {
        if (!rows.length) return;
        if (!window.confirm(`${rows.length} Anmeldung(en) dieses Events wirklich löschen?`)) return;
        try {
            await Promise.all(rows.map((row) => deleteDoc(doc(db, 'online_event_anmeldungen', row.id))));
            setSignupsOpen(false);
        } catch (error) {
            console.error('Anmeldungen konnten nicht gelöscht werden', error);
            window.alert('Löschen fehlgeschlagen. Bitte erneut versuchen.');
        }
    };

    const uploadFileToStorage = async (file: File, folder: string) => {
        const fileRef = ref(storage, `system_events/${folder}/${Date.now()}_${file.name}`);
        await uploadBytes(fileRef, file);
        return await getDownloadURL(fileRef);
    };

    const deleteOldStorageImage = async (url: string) => {
        await deleteStorageFileByDownloadUrl(url);
    };

    const handleTestReminder = async () => {
        if (!testEmail || !testEmail.includes('@')) {
            alert("Bitte eine gültige Test-E-Mail eingeben.");
            return;
        }
        if (!formData.title) {
            alert("Bitte gib zumindest einen Titel für das Event ein, bevor du testest.");
            return;
        }
        if (topicImageFile && !formData.imageUrl) {
            alert("Das Themenbild ist nur lokal ausgewählt. Bitte zuerst speichern, sonst fehlt es in der E-Mail.");
            return;
        }

        setIsTestSending(true);
        try {
            const functionsInstance = getFunctions(db.app, 'europe-west1');
            const testFn = httpsCallable(functionsInstance, 'testEventReminderMail');
            const admin = useAdminStore.getState();
            if (!admin.crmClients?.length) {
                await admin.fetchClients();
            }
            const names = await loadMailRecipientNames(
                useAdminStore.getState().crmClients,
                useAdminStore.getState().pendingApprovals
            );
            const firstName = names[testEmail.toLowerCase().trim()] || '';

            // titleImage und imageUrl getrennt — Titelbild NIEMALS als Themenbild-Fallback
            await testFn({
                testEmail: testEmail,
                firstName,
                eventData: {
                    ...formData,
                    titleImage: formData.titleImage || '',
                    imageUrl: formData.imageUrl || '',
                    topicImage: formData.imageUrl || '',
                    currentTopic: formData.currentTopic || '',
                    description: formData.description || '',
                }
            });
            alert(`Test-Erinnerung erfolgreich an ${testEmail} versendet!`);
            setTestEmail('');
        } catch (error) {
            console.error("Fehler beim Testversand:", error);
            alert("Fehler beim Testversand. Bitte in die Konsole schauen.");
        } finally {
            setIsTestSending(false);
        }
    };

    const handleManualMassEmail = async (ev: EventModel) => {
        if (!window.confirm(`Möchtest du JETZT eine echte Erinnerungs-E-Mail für "${ev.title}" an ALLE berechtigten Mandanten senden?`)) return;

        setIsMassSending(ev.id);
        try {
            const functionsInstance = getFunctions(db.app, 'europe-west1');
            const triggerFn = httpsCallable(functionsInstance, 'sendManualEventReminder');
            const admin = useAdminStore.getState();
            if (!admin.crmClients?.length) {
                await admin.fetchClients();
            }
            const recipientNames = await loadMailRecipientNames(
                useAdminStore.getState().crmClients,
                useAdminStore.getState().pendingApprovals
            );

            await triggerFn({
                eventId: ev.id,
                recipientNames,
                eventData: {
                    ...ev,
                    titleImage: ev.titleImage || '',
                    imageUrl: ev.imageUrl || '',
                    topicImage: ev.imageUrl || '',
                    currentTopic: ev.currentTopic || '',
                },
            });
            alert(`Erinnerungs-Mail für "${ev.title}" wurde erfolgreich gesendet!`);
        } catch (error) {
            console.error("Fehler beim Massenversand:", error);
            alert("Fehler beim Senden. Vermutlich fehlt noch die Backend-Funktion oder ein Timeout.");
        } finally {
            setIsMassSending(null);
        }
    };

    const handleSave = async () => {
        if (!formData.title || !formData.baseDate) return;
        const cleanTargetAudience = normalizeAudienceKeysForForm(formData.targetAudience);
        if (cleanTargetAudience.length === 0) {
            alert('Bitte mindestens eine Zielgruppe anhaken (Mandanten / Intern oder Externe).');
            return;
        }
        setIsUploading(true);
        
        let finalTitleImage = formData.titleImage;
        let finalTopicImage = formData.imageUrl;
        let baseDate = ensureISOFormat(formData.baseDate);
        let overrideDate = ensureISOFormat(formData.overrideDate);

        try {
            if (titleImageFile) {
                if (editingId && formData.titleImage) await deleteOldStorageImage(formData.titleImage);
                finalTitleImage = await uploadFileToStorage(titleImageFile, 'title_images');
            }

            if (topicImageFile) {
                if (editingId && formData.imageUrl) await deleteOldStorageImage(formData.imageUrl);
                finalTopicImage = await uploadFileToStorage(topicImageFile, 'topic_images');
            }

            // Wiederkehrend + Datum in der Vergangenheit: vor dem Speichern auf nächsten Termin schieben.
            // Monatsinhalt bleibt hier erhalten — Admin pflegt bewusst das Thema für den Folgetermin.
            if (formData.isRecurring) {
                const activeRaw = overrideDate || baseDate;
                const activeDate = parseLocalEventDate(activeRaw);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                if (activeDate && activeDate < today) {
                    baseDate = calculateNextOccurrence(formData.recurrencePattern || 'fixed', baseDate);
                    overrideDate = '';
                }
            }

            const boundActive =
                parseLocalEventDate(overrideDate || baseDate);
            const descriptionClean = isQuillDescriptionEmpty(formData.description || '')
                ? ''
                : formData.description || '';
            const hasMonthly =
                !!(String(formData.currentTopic || '').trim()
                    || descriptionClean
                    || String(finalTopicImage || '').trim());

            const payload = { 
                ...formData, 
                baseDate,
                overrideDate,
                targetAudience: cleanTargetAudience,
                titleImage: finalTitleImage, 
                imageUrl: finalTopicImage,
                topicImage: finalTopicImage,
                currentTopic: formData.currentTopic || '',
                description: descriptionClean,
                publicSpeakers: Array.isArray(formData.publicSpeakers)
                    ? formData.publicSpeakers.map((s) => ({
                          ...s,
                          name: normalizeEventSpeakerName(String(s?.name || '')),
                      }))
                    : [],
                subtitle: formData.subtitle || '', 
                eventType: formData.eventType || 'Live-Seminar',
                recurrencePattern: formData.recurrencePattern || 'fixed',
                isPaidEvent: formData.isPaidEvent || false,
                registrationUrl: formData.registrationUrl || '',
                isArchived: false,
                archivedAt: '',
                monthlyContentBoundDate: hasMonthly && boundActive ? toEventDateISO(boundActive) : '',
                isPublicOnlineEvent: !!formData.isPublicOnlineEvent,
                publicSlug: (formData.publicSlug || slugifyEventTitle(formData.title)).trim().toLowerCase(),
                publicTeaser: formData.publicTeaser || '',
                publicBullets: Array.isArray(formData.publicBullets) ? formData.publicBullets : [],
                publicCtaLabel: formData.publicCtaLabel || '',
                publicThankYouText: formData.publicThankYouText || '',
                publicFormat: formData.publicFormat || formData.eventType || 'Online-Livestream',
                willBeRecorded: formData.willBeRecorded === true ? true : formData.willBeRecorded === false ? false : null,
                monthlyWillBeRecorded: formData.monthlyWillBeRecorded === true ? true : formData.monthlyWillBeRecorded === false ? false : null,
            };

            if (payload.isPublicOnlineEvent && !payload.publicSlug) {
                alert('Bitte einen URL-Slug für die öffentliche Landingpage vergeben.');
                setIsUploading(false);
                return;
            }

            let savedId = editingId || '';
            if (editingId) {
                await updateEvent(editingId, payload);
            } else {
                const newId = await addEvent(payload);
                savedId = typeof newId === 'string' ? newId : '';
            }

            try {
                await syncPublicOnlineEvent({
                    slug: payload.publicSlug || slugifyEventTitle(payload.title),
                    previousSlug: previousPublicSlug || null,
                    publish: !!payload.isPublicOnlineEvent,
                    event: { ...payload, id: savedId },
                });
            } catch (syncErr) {
                console.error('Public-Landing Sync fehlgeschlagen:', syncErr);
                alert('Event gespeichert, aber Public-Landing Sync fehlgeschlagen. Bitte erneut speichern.');
            }
            
            setView('list');
            setPreviousPublicSlug('');
            setSlugManual(false);
        } catch (err) {
            console.error("Speicher-Fehler:", err);
            alert("Fehler beim Speichern. Prüfe die Konsole.");
        } finally {
            setIsUploading(false);
            setTitleImageFile(null);
            setTopicImageFile(null);
        }
    };

    const handleEdit = (ev: EventModel) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, ...rest } = ev;
        setFormData({ 
            ...initialFormState, 
            ...rest, 
            baseDate: ensureISOFormat(rest.baseDate) || new Date().toISOString().split('T')[0],
            overrideDate: ensureISOFormat(rest.overrideDate),
            eventType: rest.eventType || 'Live-Seminar',
            // Gespeicherte Zielgruppen exakt übernehmen (nur Key-Normalisierung, kein Default-Fill)
            targetAudience: normalizeAudienceKeysForForm(rest.targetAudience),
            isPublicOnlineEvent: !!rest.isPublicOnlineEvent,
            publicSlug: rest.publicSlug || '',
            publicTeaser: rest.publicTeaser || '',
            publicBullets: Array.isArray(rest.publicBullets) ? rest.publicBullets : [],
            publicSpeakers: Array.isArray(rest.publicSpeakers)
                ? rest.publicSpeakers.map((s) => ({
                      ...s,
                      name: normalizeEventSpeakerName(String(s?.name || '')),
                  }))
                : [],
            publicCtaLabel: rest.publicCtaLabel || initialFormState.publicCtaLabel,
            publicThankYouText: rest.publicThankYouText || initialFormState.publicThankYouText,
            publicFormat: rest.publicFormat || rest.eventType || 'Online-Livestream',
            willBeRecorded: rest.willBeRecorded === true ? true : rest.willBeRecorded === false ? false : null,
            monthlyWillBeRecorded: rest.monthlyWillBeRecorded === true ? true : rest.monthlyWillBeRecorded === false ? false : null,
        });
        setEditingId(ev.id);
        setPreviousPublicSlug(rest.publicSlug || '');
        setSlugManual(!!rest.publicSlug);
        setTitleImageFile(null);
        setTopicImageFile(null);
        setLinkCopied(false);
        setGuestSpeakerName('');
        setGuestSpeakerRole('');
        setSignupsOpen(false);
        setView('form');
    };

    const handleCreateNew = () => {
        setFormData(initialFormState);
        setEditingId(null);
        setPreviousPublicSlug('');
        setSlugManual(false);
        setTitleImageFile(null);
        setTopicImageFile(null);
        setLinkCopied(false);
        setGuestSpeakerName('');
        setGuestSpeakerRole('');
        setSignupsOpen(false);
        setView('form');
    };

    /** Archiv/Eintrag als Vorlage: neues Event, Original bleibt unverändert */
    const handleDuplicateAsTemplate = (ev: EventModel) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, isArchived, archivedAt, ...rest } = ev as EventModel & { isArchived?: boolean; archivedAt?: string };
        setFormData({
            ...initialFormState,
            ...rest,
            title: rest.title || '',
            baseDate: new Date().toISOString().split('T')[0],
            overrideDate: '',
            eventType: rest.eventType || 'Live-Seminar',
            isArchived: false,
            archivedAt: '',
            isPublicOnlineEvent: false,
            publicSlug: '',
            targetAudience: normalizeAudienceKeysForForm(rest.targetAudience).length
                ? normalizeAudienceKeysForForm(rest.targetAudience)
                : ['mandanten'],
        });
        setEditingId(null);
        setPreviousPublicSlug('');
        setSlugManual(false);
        setLinkCopied(false);
        setGuestSpeakerName('');
        setGuestSpeakerRole('');
        setTitleImageFile(null);
        setTopicImageFile(null);
        setListTab('active');
        setSignupsOpen(false);
        setView('form');
    };

    const handleCopyPublicLink = async () => {
        const slug = (formData.publicSlug || slugifyEventTitle(formData.title)).trim();
        if (!slug) return;
        const url = publicEventPreviewUrl(slug);
        try {
            await navigator.clipboard.writeText(url);
            setLinkCopied(true);
            window.setTimeout(() => setLinkCopied(false), 1800);
        } catch {
            alert(url);
        }
    };

    const handleImageRemove = async (type: 'title' | 'topic') => {
        if (!window.confirm("Bild sofort löschen? Dies entfernt das Bild endgültig aus der Datenbank.")) return;
        
        const urlToDelete = type === 'title' ? formData.titleImage : formData.imageUrl;
        if (urlToDelete) await deleteOldStorageImage(urlToDelete);

        setFormData(prev => ({
            ...prev,
            [type === 'title' ? 'titleImage' : 'imageUrl']: ''
        }));
        
        if (type === 'title') setTitleImageFile(null);
        if (type === 'topic') setTopicImageFile(null);
    };

    return (
        <div className="fixed inset-0 z-[100000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8">
            <div className="bg-[#0f1015] border border-slate-800 rounded-2xl w-full max-w-4xl shadow-2xl flex flex-col max-h-[90vh] animate-fade-in relative overflow-hidden">
                
                {/* HEADER */}
                <div className="flex items-center justify-between p-6 border-b border-slate-800 bg-[#15171e]">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-slate-800/50 rounded-xl flex items-center justify-center border border-slate-700">
                            <CalendarIcon size={24} className="text-[#E6C299]" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-white tracking-widest uppercase">
                                Seminar-Manager
                            </h2>
                            <p className="text-xs text-slate-400 font-bold">Zentrale Verwaltung für Online-Events & Seminare</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        {view === 'list' && (
                            <button onClick={handleCreateNew} className="flex items-center gap-2 bg-[#E6C299] hover:bg-white text-black px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest transition-colors shadow-[0_0_15px_rgba(230,194,153,0.3)]">
                                <PlusIcon size={16} /> Neues Event
                            </button>
                        )}
                        <button onClick={onClose} className="p-2 bg-slate-800 hover:bg-slate-700 rounded-full text-slate-400 transition-colors">
                            <XIcon size={20} />
                        </button>
                    </div>
                </div>

                {/* CONTENT LAYER */}
                <div className="flex-1 overflow-y-auto p-6 bg-[#08090b]">
                    {isLoading && view === 'list' ? (
                        <div className="py-12 flex justify-center text-slate-500 font-bold uppercase tracking-widest animate-pulse">Lade Events...</div>
                    ) : view === 'list' ? (
                        <div className="space-y-4">
                            <div className="flex gap-2 mb-2">
                                <button
                                    type="button"
                                    onClick={() => setListTab('active')}
                                    className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border transition-colors ${listTab === 'active' ? 'bg-[#E6C299] text-black border-[#E6C299]' : 'bg-slate-900 text-slate-400 border-slate-700 hover:border-slate-500'}`}
                                >
                                    Aktuell ({events.length})
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setListTab('archive')}
                                    className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest border transition-colors ${listTab === 'archive' ? 'bg-slate-600 text-white border-slate-500' : 'bg-slate-900 text-slate-400 border-slate-700 hover:border-slate-500'}`}
                                >
                                    Archiv ({archivedEvents.length})
                                </button>
                            </div>

                            {listTab === 'archive' && (
                                <p className="text-xs text-slate-500 mb-2">
                                    Abgelaufene Einmal-Events landen automatisch hier. Serien rollen weiter — sie werden nicht archiviert.
                                </p>
                            )}

                            {(listTab === 'active' ? events : archivedEvents).length === 0 ? (
                                <div className="py-12 flex flex-col items-center text-slate-500 bg-[#15171e] rounded-xl border border-slate-800/50">
                                    <CalendarIcon size={48} className="mb-4 opacity-30" />
                                    <p className="font-bold uppercase tracking-widest">
                                        {listTab === 'active' ? 'Keine Events geplant.' : 'Archiv ist leer.'}
                                    </p>
                                </div>
                            ) : (
                                <>
                                {listTab === 'active' && events.some((ev) => shouldShowMissingTopicReminder(ev)) && (
                                    <div className="bg-amber-950/40 border border-amber-500/40 rounded-xl p-4 flex items-start gap-3">
                                        <AlertTriangleIcon size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-bold text-amber-300">Noch kein Monats-Thema hinterlegt</p>
                                            <p className="text-[11px] text-amber-200/70 mt-1 leading-relaxed">
                                                Mindestens ein Event steht in den nächsten 10 Tagen an, hat aber noch kein Thema.
                                                Bitte Monatsinhalt einpflegen, sonst bleibt die Events-Seite ohne Themenblock.
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {(listTab === 'active' ? events : archivedEvents).map(ev => {
                                    const activeDateStr = ev.overrideDate ? ev.overrideDate : ev.baseDate;
                                    const dateObj = parseLocalEventDate(getEventActiveDateRaw(ev)) ?? new Date(activeDateStr);
                                    const isOverride = !!ev.overrideDate;
                                    const eventTypeLabel = clarifyOnlineFormat(ev.eventType || 'Live-Seminar');
                                    const missingTopic = listTab === 'active' && shouldShowMissingTopicReminder(ev);
                                    const daysUntil = getDaysUntilEventDate(ev);

                                    return (
                                        <div key={ev.id} className={`bg-[#15171e] border rounded-xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:border-slate-700 transition-colors group ${missingTopic ? 'border-amber-500/50' : listTab === 'archive' ? 'border-slate-700/80 opacity-90' : 'border-slate-800'}`}>
                                            <div className="flex items-start gap-4">
                                                {ev.titleImage ? (
                                                    <div className="w-16 h-16 rounded bg-slate-800 border border-slate-700 flex-shrink-0 overflow-hidden hidden sm:block">
                                                        <img src={ev.titleImage} alt="" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                                    </div>
                                                ) : (
                                                    <div className="w-16 h-16 rounded bg-slate-800/50 border border-slate-700 flex-shrink-0 hidden sm:flex items-center justify-center">
                                                        <ImageIcon size={20} className="text-slate-600" />
                                                    </div>
                                                )}
                                                
                                                <div>
                                                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                        <span className="text-[10px] text-[#E6C299] font-black uppercase tracking-widest bg-[#E6C299]/10 px-2 py-0.5 rounded border border-[#E6C299]/30">
                                                            {eventTypeLabel}
                                                        </span>
                                                        <h3 className="text-lg font-bold text-white leading-tight">{ev.title}</h3>
                                                        {listTab === 'archive' && (
                                                            <span className="bg-slate-700/50 text-slate-300 border border-slate-600 px-2 py-0.5 rounded text-[9px] uppercase tracking-widest font-black">
                                                                Archiv
                                                            </span>
                                                        )}
                                                        {ev.isRecurring && (
                                                            <span className="bg-blue-900/30 text-blue-400 border border-blue-500/30 px-2 py-0.5 rounded text-[9px] uppercase tracking-widest font-black flex items-center gap-1">
                                                                <ClockIcon size={10} /> Auto-Serie
                                                            </span>
                                                        )}
                                                        {isOverride && listTab === 'active' && (
                                                            <span className="bg-orange-900/30 text-orange-400 border border-orange-500/30 px-2 py-0.5 rounded text-[9px] uppercase tracking-widest font-black flex items-center gap-1">
                                                                <AlertTriangleIcon size={10} /> Schubser Aktiv
                                                            </span>
                                                        )}
                                                        {missingTopic && (
                                                            <span className="bg-amber-900/40 text-amber-300 border border-amber-500/40 px-2 py-0.5 rounded text-[9px] uppercase tracking-widest font-black flex items-center gap-1">
                                                                <AlertTriangleIcon size={10} />
                                                                {daysUntil === 0 ? 'Heute · kein Thema' : `Noch ${daysUntil} Tage · kein Thema`}
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    {ev.currentTopic ? (
                                                        <div className="text-sm font-bold text-slate-300 mb-2 border-l-2 border-[#E6C299] pl-3 flex items-start gap-3">
                                                            <div className="flex-1">
                                                                Thema: <span className="text-white">{ev.currentTopic}</span>
                                                            </div>
                                                            {ev.imageUrl ? (
                                                                <img src={ev.imageUrl} alt="" className="w-10 h-10 rounded object-cover border border-slate-700 flex-shrink-0" />
                                                            ) : null}
                                                        </div>
                                                    ) : missingTopic ? (
                                                        <div className="text-sm font-bold text-amber-300/90 mb-2 border-l-2 border-amber-500 pl-3">
                                                            Noch kein Monats-Thema hinterlegt
                                                        </div>
                                                    ) : null}

                                                    <div className="flex flex-wrap items-center gap-4 text-xs mt-3">
                                                        <span className={`${isOverride ? 'text-orange-400 bg-orange-500/10' : 'text-[#E6C299] bg-[#E6C299]/10'} font-mono font-bold px-2 py-1 rounded`}>
                                                            {dateObj.toLocaleDateString('de-DE')}
                                                        </span>
                                                        
                                                        <div className="flex items-center gap-1 text-slate-400 bg-slate-800 px-2 py-1 rounded font-mono">
                                                            <ClockIcon size={12} />
                                                            {ev.timeMorning} Uhr
                                                            {ev.hasDoubleSession && !ev.isPaidEvent && ` & ${ev.timeEvening} Uhr`}
                                                        </div>
                                                        {ev.isPublicOnlineEvent ? (
                                                            <span className="flex items-center gap-1 text-[#E6C299] bg-[#E6C299]/10 px-2 py-1 rounded font-bold">
                                                                <UsersIcon size={12} />
                                                                {signupCounts[String(ev.publicSlug || '').trim().toLowerCase()] || 0} Anmeldungen
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-center gap-2">
                                                {listTab === 'active' && (
                                                    <button onClick={() => handleManualMassEmail(ev)} disabled={isMassSending === ev.id} className="p-3 bg-blue-900/20 text-blue-400 hover:bg-blue-600 hover:text-white rounded-lg transition-colors border border-blue-900/50 shadow-lg" title="Mails für dieses Event manuell versenden">
                                                        <MailIcon size={16} />
                                                    </button>
                                                )}
                                                {listTab === 'archive' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDuplicateAsTemplate(ev)}
                                                        className="px-3 py-3 bg-[#E6C299]/15 text-[#E6C299] hover:bg-[#E6C299] hover:text-black rounded-lg transition-colors border border-[#E6C299]/40 shadow-lg text-[9px] font-black uppercase tracking-widest"
                                                        title="Als Vorlage duplizieren — neues Event, Archiv bleibt"
                                                    >
                                                        Vorlage
                                                    </button>
                                                )}
                                                {listTab === 'archive' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (window.confirm('Event aus dem Archiv wieder aktivieren?')) {
                                                                void restoreEventFromArchive(ev.id);
                                                            }
                                                        }}
                                                        className="px-3 py-3 bg-emerald-900/20 text-emerald-400 hover:bg-emerald-600 hover:text-white rounded-lg transition-colors border border-emerald-900/50 shadow-lg text-[9px] font-black uppercase tracking-widest"
                                                        title="Aus Archiv zurückholen"
                                                    >
                                                        Restore
                                                    </button>
                                                )}
                                                {listTab === 'active' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDuplicateAsTemplate(ev)}
                                                        className="p-3 bg-slate-800 text-[#E6C299] hover:bg-[#E6C299] hover:text-black rounded-lg transition-colors border border-slate-700 shadow-lg text-[9px] font-black uppercase tracking-widest"
                                                        title="Als Vorlage duplizieren"
                                                    >
                                                        Kopie
                                                    </button>
                                                )}
                                                {listTab === 'active' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (
                                                                window.confirm(
                                                                    'Event ins Archiv verschieben?\n\n• Bleibt erhalten (kein Löschen)\n• Öffentliche Anmeldung wird gestoppt\n• Unter „Archiv“ wiederherstellbar'
                                                                )
                                                            ) {
                                                                void archiveEvent(ev.id, ev.publicSlug);
                                                            }
                                                        }}
                                                        className="p-3 bg-amber-900/20 text-amber-300 hover:bg-amber-600 hover:text-white rounded-lg transition-colors border border-amber-900/50 shadow-lg"
                                                        title="Ins Archiv — Event bleibt erhalten, Anmeldung gestoppt"
                                                    >
                                                        <ArchiveIcon size={16} />
                                                    </button>
                                                )}
                                                <button onClick={() => handleEdit(ev)} className="p-3 bg-slate-800 text-slate-300 hover:text-white rounded-lg transition-colors border border-slate-700 shadow-lg">
                                                    <EditIcon size={16} />
                                                </button>
                                                {listTab === 'archive' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (
                                                                window.confirm(
                                                                    'ENDGÜLTIG löschen?\n\nDas Event und die Bilder werden permanent entfernt. Besser nur nutzen, wenn du es wirklich nicht mehr brauchst.'
                                                                )
                                                            ) {
                                                                void deleteEvent(ev.id, ev.titleImage, ev.imageUrl);
                                                            }
                                                        }}
                                                        className="p-3 bg-red-900/20 text-red-500 hover:bg-red-600 hover:text-white rounded-lg transition-colors border border-red-900/50 shadow-lg"
                                                        title="Endgültig löschen (nur Archiv)"
                                                    >
                                                        <TrashIcon size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                                </>
                            )}
                        </div>
                    ) : (
                        <div className="max-w-2xl mx-auto bg-[#15171e] rounded-xl p-8 border border-slate-800 shadow-2xl animate-slide-in-right">
                            <h3 className="text-sm font-black text-[#E6C299] uppercase tracking-widest mb-2 border-b border-slate-800 pb-4">
                                {editingId ? 'Event bearbeiten' : 'Neues Event erstellen'}
                            </h3>
                            {!editingId && formData.title && (
                                <p className="text-xs text-[#E6C299]/90 mb-6 bg-[#E6C299]/10 border border-[#E6C299]/25 rounded-lg px-3 py-2">
                                    Vorlage — bitte <strong>Datum</strong> und ggf. Titel anpassen, dann speichern. Das Archiv-Original bleibt unverändert.
                                </p>
                            )}
                            
                            <div className="space-y-6">
                                
                                {/* BLOCK 1: DAS GERÜST */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="md:col-span-2">
                                        <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">Titel des Seminars *</label>
                                        <input
                                            type="text"
                                            value={formData.title}
                                            onChange={e => {
                                                const title = e.target.value;
                                                setFormData(prev => ({
                                                    ...prev,
                                                    title,
                                                    publicSlug: slugManual ? prev.publicSlug : slugifyEventTitle(title),
                                                }));
                                            }}
                                            className="w-full bg-[#08090b] border border-slate-700 text-white rounded-lg p-3 text-sm focus:border-[#E6C299] outline-none transition-colors"
                                            placeholder="z.B. Tacheles Reden"
                                        />
                                    </div>
                                    <div className="md:col-span-1">
                                        <label className="text-[10px] text-[#E6C299] font-bold uppercase tracking-wider mb-2 block">Art des Events</label>
                                        <select value={formData.eventType} onChange={e => setFormData({...formData, eventType: e.target.value})} className="w-full bg-[#08090b] border border-[#E6C299]/50 text-[#E6C299] font-bold rounded-lg p-3 text-sm focus:border-[#E6C299] outline-none transition-colors">
                                            <option value="Live-Seminar">Online-Seminar</option>
                                            <option value="Live-Meeting">Online-Live-Meeting</option>
                                            <option value="Masterclass">Online-Masterclass</option>
                                            <option value="Schulung">Online-Schulung</option>
                                            <option value="Q&A Session">Online-Q&A</option>
                                            <option value="Event">Online-Event</option>
                                        </select>
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">Slogan / Untertitel</label>
                                    <textarea
                                        value={formData.subtitle}
                                        onChange={e => setFormData({...formData, subtitle: e.target.value})}
                                        rows={3}
                                        className="w-full bg-[#08090b] border border-slate-700 text-slate-300 rounded-lg p-3 text-sm focus:border-[#E6C299] outline-none transition-colors resize-y"
                                        placeholder="z.B. Kostenlos, aber unbezahlbar..."
                                    />
                                </div>

                                <div>
                                    <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">
                                        Beschreibung
                                    </label>
                                    <p className="text-[10px] text-slate-500 mb-2">
                                        Einmal pflegen: gilt für die Eventansicht im System — und für die Landing, falls das Event öffentlich ist.
                                    </p>
                                    <textarea
                                        value={formData.publicTeaser || ''}
                                        onChange={e => setFormData({ ...formData, publicTeaser: e.target.value })}
                                        rows={8}
                                        className="w-full bg-[#08090b] border border-slate-700 text-white rounded-lg p-3 text-sm focus:border-[#E6C299] outline-none resize-y"
                                        placeholder="Der ausführliche Text zum Seminar…"
                                    />
                                </div>

                                {/* FILE UPLOAD: Kalender-Titelbild */}
                                <div>
                                    <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">Standard Titelbild (Überlebt den Monatswechsel)</label>
                                    <p className="text-[10px] text-slate-500 mb-2">Quadrat oder Instagram-Hochformat — die Ansicht passt sich automatisch an.</p>
                                    <div className="flex items-center gap-4">
                                        {(titleImageFile || formData.titleImage) && (
                                            <div className="w-16 h-16 rounded bg-[#08090b] border border-slate-700 overflow-hidden relative group">
                                                <img src={titleImageFile ? URL.createObjectURL(titleImageFile) : formData.titleImage} alt="Preview" className="w-full h-full object-cover" />
                                                <button onClick={() => handleImageRemove('title')} className="absolute inset-0 bg-red-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <TrashIcon size={16} />
                                                </button>
                                            </div>
                                        )}
                                        <div className="flex-1">
                                            <input type="file" accept="image/*" onChange={(e) => { if(e.target.files && e.target.files[0]) setTitleImageFile(e.target.files[0]); }} className="hidden" id="upload-title" />
                                            <label htmlFor="upload-title" className="flex items-center justify-center gap-2 w-full bg-slate-800 hover:bg-slate-700 border border-slate-600 border-dashed text-slate-300 rounded-lg p-3 text-sm font-bold uppercase tracking-widest cursor-pointer transition-colors">
                                                <UploadCloudIcon size={18} /> {titleImageFile || formData.titleImage ? 'Neues Titelbild wählen' : 'Bild hochladen'}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">Stamm-Datum (Tag) *</label>
                                        <input type="date" value={formData.baseDate} onChange={e => setFormData({...formData, baseDate: e.target.value})} className="w-full bg-[#08090b] border border-slate-700 text-white font-mono rounded-lg p-3 text-sm focus:border-[#E6C299] outline-none transition-colors" />
                                    </div>
                                </div>

                                {/* TICKET / ANMELDUNG */}
                                <div className="bg-[#E6C299]/5 border border-[#E6C299]/20 rounded-xl p-5 space-y-4">
                                    <label className="flex items-center cursor-pointer group pb-4 border-b border-[#E6C299]/10">
                                        <div className="flex items-center h-6">
                                            <input type="checkbox" checked={formData.isPaidEvent} onChange={e => setFormData({...formData, isPaidEvent: e.target.checked, hasDoubleSession: e.target.checked ? false : formData.hasDoubleSession})} className="w-5 h-5 rounded border-slate-600 text-[#E6C299] focus:ring-[#E6C299] bg-[#08090b]" />
                                        </div>
                                        <div className="ml-3">
                                            <span className="text-sm font-bold text-[#E6C299] transition-colors">Externes Event / Ticket-Verkauf</span>
                                            <p className="text-[10px] text-slate-400 mt-1">Statt Zoom-Links führt der Button die Mandanten zu einer Landingpage oder einem Stripe-Formular.</p>
                                        </div>
                                    </label>

                                    {formData.isPaidEvent ? (
                                        <div className="animate-fade-in pt-2">
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                                <div>
                                                    <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">Uhrzeit</label>
                                                    <input type="time" value={formData.timeMorning} onChange={e => setFormData({...formData, timeMorning: e.target.value})} className="w-full bg-[#08090b] border border-slate-700 text-white font-mono rounded-lg p-3 text-sm focus:border-[#E6C299] outline-none transition-colors" />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="text-[10px] text-[#E6C299] font-bold uppercase tracking-wider mb-2 block flex items-center gap-1"><ExternalLinkIcon size={12}/> Link zur Landingpage / Formular *</label>
                                                    <input type="url" value={formData.registrationUrl} onChange={e => setFormData({...formData, registrationUrl: e.target.value})} className="w-full bg-[#08090b] border border-[#E6C299]/50 text-white rounded-lg p-3 text-sm focus:border-[#E6C299] outline-none font-mono" placeholder="https://..." />
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="animate-fade-in pt-2 space-y-4">
                                            <label className="flex items-center cursor-pointer group">
                                                <div className="flex items-center h-6">
                                                    <input type="checkbox" checked={formData.hasDoubleSession} onChange={e => setFormData({...formData, hasDoubleSession: e.target.checked})} className="w-5 h-5 rounded border-slate-600 text-white focus:ring-slate-500 bg-[#08090b]" />
                                                </div>
                                                <div className="ml-3">
                                                    <span className="text-sm font-bold text-white transition-colors">Doppel-Termin aktivieren</span>
                                                </div>
                                            </label>

                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                                                <div>
                                                    <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">Uhrzeit (Morgens) *</label>
                                                    <input type="time" value={formData.timeMorning} onChange={e => setFormData({...formData, timeMorning: e.target.value})} className="w-full bg-[#08090b] border border-slate-700 text-white font-mono rounded-lg p-3 text-sm focus:border-[#E6C299] outline-none transition-colors" />
                                                </div>
                                                <div className="md:col-span-2">
                                                    <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">Zoom-Link (Morgens)</label>
                                                    <div className="relative">
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><ExternalLinkIcon size={14} className="text-slate-500" /></div>
                                                        <input type="url" value={formData.linkMorning} onChange={e => setFormData({...formData, linkMorning: e.target.value})} className="w-full pl-9 pr-3 py-3 bg-[#08090b] border border-slate-700 text-white rounded-lg text-sm focus:border-[#E6C299] outline-none font-mono" placeholder="https://zoom..." />
                                                    </div>
                                                </div>
                                            </div>

                                            {formData.hasDoubleSession && (
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end animate-fade-in pt-4 border-t border-slate-700/50">
                                                    <div>
                                                        <label className="text-[10px] text-blue-400 font-bold uppercase tracking-wider mb-2 block">Uhrzeit (Abends)</label>
                                                        <input type="time" value={formData.timeEvening} onChange={e => setFormData({...formData, timeEvening: e.target.value})} className="w-full bg-[#08090b] border border-blue-900/50 text-white font-mono rounded-lg p-3 text-sm focus:border-blue-500 outline-none transition-colors" />
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <label className="text-[10px] text-blue-400 font-bold uppercase tracking-wider mb-2 block">Zoom-Link (Abends)</label>
                                                        <div className="relative">
                                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><ExternalLinkIcon size={14} className="text-blue-500/50" /></div>
                                                            <input type="url" value={formData.linkEvening} onChange={e => setFormData({...formData, linkEvening: e.target.value})} className="w-full pl-9 pr-3 py-3 bg-[#08090b] border border-blue-900/50 text-white rounded-lg text-sm focus:border-blue-500 outline-none font-mono" placeholder="https://zoom..." />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* PUBLIC ONLINE-EVENT LANDING */}
                                <div className="bg-slate-800/40 border border-slate-700/80 rounded-xl p-5 space-y-4">
                                    <label className="flex items-center cursor-pointer group pb-4 border-b border-slate-700/60">
                                        <div className="flex items-center h-6">
                                            <input
                                                type="checkbox"
                                                checked={!!formData.isPublicOnlineEvent}
                                                onChange={e => {
                                                    const on = e.target.checked;
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        isPublicOnlineEvent: on,
                                                        publicSlug:
                                                            prev.publicSlug ||
                                                            slugifyEventTitle(prev.title),
                                                    }));
                                                    if (on && !slugManual && !formData.publicSlug) {
                                                        setSlugManual(false);
                                                    }
                                                }}
                                                className="w-5 h-5 rounded border-slate-600 text-[#E6C299] focus:ring-[#E6C299] bg-[#08090b]"
                                            />
                                        </div>
                                        <div className="ml-3">
                                            <span className="text-sm font-bold text-slate-100 transition-colors">
                                                Öffentliches Online-Event
                                            </span>
                                            <p className="text-[10px] text-slate-400 mt-1">
                                                Schaltet eine öffentliche Landingpage unter /event/&#123;slug&#125; frei. Der Zugangslink steht in der Bestätigungsmail, nicht auf der Seite.
                                            </p>
                                        </div>
                                    </label>

                        {formData.isPublicOnlineEvent && (
                            <div className="animate-fade-in space-y-4 pt-1">
                                {(() => {
                                    const currentSlug = (formData.publicSlug || slugifyEventTitle(formData.title)).trim().toLowerCase();
                                    const currentSignups = signupsBySlug[currentSlug] || [];
                                    return (
                                        <div className="rounded-lg border border-[#E6C299]/30 bg-[#E6C299]/5 p-4">
                                            <button
                                                type="button"
                                                aria-expanded={signupsOpen}
                                                onClick={() => setSignupsOpen((open) => !open)}
                                                className="w-full flex items-center justify-between gap-3 text-left"
                                            >
                                                <div>
                                                    <p className="text-[10px] text-[#E6C299] font-black uppercase tracking-widest mb-1">Landing-Anmeldungen</p>
                                                    <p className="text-2xl font-black text-white leading-none">
                                                        {currentSignups.length}{' '}
                                                        <span className="text-sm font-bold text-slate-400">
                                                            {currentSignups.length === 1 ? 'Person' : 'Personen'}
                                                        </span>
                                                    </p>
                                                    {!signupsOpen && (
                                                        <p className="text-[10px] text-slate-500 mt-2">Zum Anzeigen und Löschen aufklappen.</p>
                                                    )}
                                                </div>
                                                <ChevronIcon
                                                    className={`w-5 h-5 text-[#E6C299] shrink-0 transition-transform ${signupsOpen ? 'rotate-180' : ''}`}
                                                />
                                            </button>
                                            {signupsOpen && (
                                                <div className="mt-3 pt-3 border-t border-[#E6C299]/20 space-y-3">
                                                    {currentSignups.length > 0 ? (
                                                        <>
                                                            <ul className="max-h-48 overflow-y-auto space-y-1.5">
                                                                {currentSignups.map((row) => (
                                                                    <li key={row.id} className="flex items-center gap-2">
                                                                        <span className="text-[11px] text-slate-300 font-mono truncate flex-1 min-w-0">
                                                                            {row.firstName ? `${row.firstName} · ` : ''}{row.email}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleDeleteLandingSignup(row)}
                                                                            className="shrink-0 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/15"
                                                                        >
                                                                            Löschen
                                                                        </button>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDeleteAllLandingSignups(currentSignups)}
                                                                className="text-[10px] font-black uppercase tracking-widest text-rose-400 hover:text-rose-300"
                                                            >
                                                                Alle Anmeldungen löschen
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <p className="text-[11px] text-slate-500">Noch keine Anmeldungen über die Landingpage.</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                                            {!String(formData.linkMorning || '').trim() ? (
                                                <p className="text-[11px] text-amber-300/90 bg-amber-900/20 border border-amber-500/30 rounded-lg px-3 py-2">
                                                    Ohne Meeting-Link (oben) geht in der Bestätigungsmail kein Zugangslink raus.
                                                </p>
                                            ) : null}
                                            <div>
                                                <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">
                                                    URL-Slug *
                                                </label>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[11px] text-slate-500 font-mono shrink-0">/event/</span>
                                                    <input
                                                        type="text"
                                                        value={formData.publicSlug || ''}
                                                        onChange={e => {
                                                            setSlugManual(true);
                                                            const raw = e.target.value
                                                                .toLowerCase()
                                                                .replace(/[^a-z0-9-]+/g, '-')
                                                                .replace(/^-+|-+$/g, '');
                                                            setFormData(prev => ({ ...prev, publicSlug: raw }));
                                                        }}
                                                        className="w-full bg-[#08090b] border border-slate-700 text-white font-mono rounded-lg p-3 text-sm focus:border-[#E6C299] outline-none"
                                                        placeholder="kickoff-2026"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">
                                                    Preview-Link
                                                </label>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <code className="flex-1 min-w-0 truncate bg-[#08090b] border border-slate-700 text-[#E6C299] font-mono text-[11px] rounded-lg px-3 py-2.5">
                                                        {publicEventPreviewUrl(
                                                            formData.publicSlug || slugifyEventTitle(formData.title)
                                                        )}
                                                    </code>
                                                    <div className="relative">
                                                        <button
                                                            type="button"
                                                            onClick={handleCopyPublicLink}
                                                            className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-200 px-3 py-2.5 rounded-lg text-[10px] font-bold uppercase tracking-widest transition-colors"
                                                        >
                                                            {linkCopied ? <CheckIcon size={14} className="text-emerald-400" /> : <CopyIcon size={14} />}
                                                            {linkCopied ? 'Kopiert' : 'Link kopieren'}
                                                        </button>
                                                        {linkCopied && (
                                                            <span className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-emerald-900/90 text-emerald-200 text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded border border-emerald-700/50">
                                                                In Zwischenablage
                                                            </span>
                                                        )}
                                                    </div>
                                                    <a
                                                        href={publicEventOpenUrl(
                                                            formData.publicSlug || slugifyEventTitle(formData.title)
                                                        )}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center justify-center bg-slate-800 hover:bg-slate-700 border border-slate-600 text-[#E6C299] p-2.5 rounded-lg transition-colors"
                                                        title="Landingpage öffnen"
                                                    >
                                                        <ExternalLinkIcon size={14} />
                                                    </a>
                                                </div>
                                            </div>

                                            <div>
                                                <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">
                                                    Das erwartet dich
                                                </label>
                                                <p className="text-[10px] text-slate-500 mb-2">
                                                    Ebenfalls Landingpage und Eventansicht im System.
                                                </p>
                                                <div className="space-y-2">
                                                    {(formData.publicBullets || []).map((bullet, idx) => (
                                                        <div key={idx} className="flex items-center gap-2">
                                                            <input
                                                                type="text"
                                                                value={bullet}
                                                                onChange={e => {
                                                                    const next = [...(formData.publicBullets || [])];
                                                                    next[idx] = e.target.value;
                                                                    setFormData({ ...formData, publicBullets: next });
                                                                }}
                                                                className="flex-1 bg-[#08090b] border border-slate-700 text-white rounded-lg p-2.5 text-sm focus:border-[#E6C299] outline-none"
                                                                placeholder={`Punkt ${idx + 1}`}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    const next = (formData.publicBullets || []).filter((_, i) => i !== idx);
                                                                    setFormData({ ...formData, publicBullets: next });
                                                                }}
                                                                className="p-2 text-slate-500 hover:text-red-400 transition-colors"
                                                            >
                                                                <TrashIcon size={14} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setFormData({
                                                                ...formData,
                                                                publicBullets: [...(formData.publicBullets || []), ''],
                                                            })
                                                        }
                                                        className="text-[10px] font-bold uppercase tracking-widest text-slate-400 hover:text-[#E6C299] flex items-center gap-1"
                                                    >
                                                        <PlusIcon size={12} /> Bullet hinzufügen
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div>
                                                    <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">
                                                        CTA-Label
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={formData.publicCtaLabel || ''}
                                                        onChange={e => setFormData({ ...formData, publicCtaLabel: e.target.value })}
                                                        className="w-full bg-[#08090b] border border-slate-700 text-white rounded-lg p-3 text-sm focus:border-[#E6C299] outline-none"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">
                                                        Format (Landing)
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={formData.publicFormat || ''}
                                                        onChange={e => setFormData({ ...formData, publicFormat: e.target.value })}
                                                        className="w-full bg-[#08090b] border border-slate-700 text-white rounded-lg p-3 text-sm focus:border-[#E6C299] outline-none"
                                                        placeholder="Online-Livestream"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="bg-slate-800/40 border border-slate-700/80 rounded-xl p-5 space-y-3">
                                    <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                                        Referenten
                                    </label>
                                    <p className="text-[10px] text-slate-500">
                                        Gilt für die Eventansicht im System — und für die Landing, falls das Event öffentlich ist. Ohne Auswahl bei öffentlicher Landing: „Das PreisExpert Team“.
                                    </p>
                                    <div className="flex flex-wrap gap-4 mb-1">
                                        {CORE_EVENT_SPEAKERS.map(opt => {
                                            const selected = (formData.publicSpeakers || []).some(
                                                s => normalizeEventSpeakerName(s.name) === opt.name
                                            );
                                            return (
                                                <label
                                                    key={opt.name}
                                                    className="flex items-center gap-2 cursor-pointer text-sm font-bold text-slate-300 hover:text-white transition-colors"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={selected}
                                                        onChange={() => {
                                                            const current = formData.publicSpeakers || [];
                                                            const next = selected
                                                                ? current.filter(
                                                                      s =>
                                                                          normalizeEventSpeakerName(s.name) !==
                                                                          opt.name
                                                                  )
                                                                : [
                                                                      ...current,
                                                                      { name: opt.name, role: opt.role, bio: '' },
                                                                  ];
                                                            setFormData({ ...formData, publicSpeakers: next });
                                                        }}
                                                        className="w-4 h-4 rounded border-slate-600 bg-[#08090b] text-[#E6C299] focus:ring-[#E6C299]"
                                                    />
                                                    {opt.name}
                                                </label>
                                            );
                                        })}
                                    </div>
                                    {(formData.publicSpeakers || []).filter(
                                        s => !CORE_SPEAKER_NAMES.includes(normalizeEventSpeakerName(s.name))
                                    ).length > 0 && (
                                        <div className="flex flex-wrap gap-2">
                                            {(formData.publicSpeakers || [])
                                                .filter(
                                                    s =>
                                                        !CORE_SPEAKER_NAMES.includes(
                                                            normalizeEventSpeakerName(s.name)
                                                        )
                                                )
                                                .map(s => (
                                                    <span
                                                        key={s.name}
                                                        className="inline-flex items-center gap-2 text-[11px] font-bold text-slate-200 bg-slate-800 border border-slate-600 rounded-lg px-2 py-1"
                                                    >
                                                        {s.name}{s.role ? ` · ${s.role}` : ''}
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                setFormData({
                                                                    ...formData,
                                                                    publicSpeakers: (formData.publicSpeakers || []).filter(
                                                                        x => x.name !== s.name
                                                                    ),
                                                                })
                                                            }
                                                            className="text-slate-500 hover:text-red-400"
                                                        >
                                                            <TrashIcon size={12} />
                                                        </button>
                                                    </span>
                                                ))}
                                        </div>
                                    )}
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input
                                            type="text"
                                            value={guestSpeakerName}
                                            onChange={e => setGuestSpeakerName(e.target.value)}
                                            className="flex-1 bg-[#08090b] border border-slate-700 text-white rounded-lg p-2.5 text-sm focus:border-[#E6C299] outline-none"
                                            placeholder="Externer Referent (Name)"
                                        />
                                        <input
                                            type="text"
                                            value={guestSpeakerRole}
                                            onChange={e => setGuestSpeakerRole(e.target.value)}
                                            className="flex-1 bg-[#08090b] border border-slate-700 text-white rounded-lg p-2.5 text-sm focus:border-[#E6C299] outline-none"
                                            placeholder="Rolle (optional)"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const name = guestSpeakerName.trim();
                                                if (!name) return;
                                                const current = formData.publicSpeakers || [];
                                                if (current.some(s => s.name.toLowerCase() === name.toLowerCase())) {
                                                    setGuestSpeakerName('');
                                                    setGuestSpeakerRole('');
                                                    return;
                                                }
                                                setFormData({
                                                    ...formData,
                                                    publicSpeakers: [
                                                        ...current,
                                                        { name, role: guestSpeakerRole.trim() || undefined, bio: '' },
                                                    ],
                                                });
                                                setGuestSpeakerName('');
                                                setGuestSpeakerRole('');
                                            }}
                                            className="text-[10px] font-bold uppercase tracking-widest text-slate-300 hover:text-[#E6C299] border border-slate-600 rounded-lg px-3 py-2"
                                        >
                                            Hinzufügen
                                        </button>
                                    </div>
                                </div>

                                <div className="bg-slate-800/40 border border-slate-700/80 rounded-xl p-5 space-y-3">
                                    <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                                        Aufzeichnung
                                    </label>
                                    <p className="text-[10px] text-slate-500">
                                        Standard für diese Serie. Ein Haken im Monatsbeitrag unten überschreibt das für den aktuellen Termin.
                                    </p>
                                    <div className="flex flex-wrap gap-5">
                                        <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-slate-300 hover:text-white">
                                            <input
                                                type="checkbox"
                                                checked={formData.willBeRecorded === true}
                                                onChange={() =>
                                                    setFormData({
                                                        ...formData,
                                                        willBeRecorded: formData.willBeRecorded === true ? null : true,
                                                    })
                                                }
                                                className="w-4 h-4 rounded border-slate-600 bg-[#08090b] text-[#E6C299] focus:ring-[#E6C299]"
                                            />
                                            Wird aufgezeichnet
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-slate-300 hover:text-white">
                                            <input
                                                type="checkbox"
                                                checked={formData.willBeRecorded === false}
                                                onChange={() =>
                                                    setFormData({
                                                        ...formData,
                                                        willBeRecorded: formData.willBeRecorded === false ? null : false,
                                                    })
                                                }
                                                className="w-4 h-4 rounded border-slate-600 bg-[#08090b] text-[#E6C299] focus:ring-[#E6C299]"
                                            />
                                            Wird nicht aufgezeichnet
                                        </label>
                                    </div>
                                </div>

                                {/* SMART ROLLOVER & OVERRIDE */}
                                <div className="bg-blue-900/10 border border-blue-900/30 rounded-xl p-5">
                                    <label className="flex items-start cursor-pointer group mb-4">
                                        <div className="flex items-center h-6">
                                            <input type="checkbox" checked={formData.isRecurring} onChange={e => setFormData({...formData, isRecurring: e.target.checked})} className="w-5 h-5 rounded border-slate-600 text-blue-500 focus:ring-blue-500 bg-[#08090b]" />
                                        </div>
                                        <div className="ml-3">
                                            <span className="text-sm font-bold text-blue-400 group-hover:text-blue-300 transition-colors">Smart Rollover (Auto-Serie)</span>
                                            <p className="text-[10px] text-slate-500 mt-1 leading-tight">Nach Ablauf schiebt das System automatisch auf den nächsten Termin und leert Thema, Beschreibung, Themenbild und die Monats-Aufzeichnung. Neuen Monatsinhalt bitte für den Folgetermin neu einpflegen.</p>
                                        </div>
                                    </label>

                                    {formData.isRecurring && (
                                        <div className="pl-8 space-y-4 animate-fade-in border-l-2 border-blue-500/30 ml-2">
                                            <div>
                                                <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">Rhythmus-Muster für nächsten Monat</label>
                                                <select 
                                                    value={formData.recurrencePattern} 
                                                    onChange={e => setFormData({...formData, recurrencePattern: e.target.value})} 
                                                    className="w-full bg-[#08090b] border border-blue-900/50 text-white rounded-lg p-3 text-sm focus:border-blue-500 outline-none transition-colors font-bold"
                                                >
                                                    <option value="fixed">Festes Datum (Tag im Monat beibehalten)</option>
                                                    <option value="first-monday">Immer der 1. Montag im Monat</option>
                                                    <option value="second-monday">Immer der 2. Montag im Monat</option>
                                                    <option value="first-wednesday">Immer der 1. Mittwoch im Monat</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="text-[10px] text-orange-400 font-bold uppercase tracking-wider mb-2 block flex items-center gap-1">
                                                    <AlertTriangleIcon size={12} /> Ausweichtermin (Feiertags-Schubser)
                                                </label>
                                                <input 
                                                    type="date" 
                                                    value={formData.overrideDate} 
                                                    onChange={e => setFormData({...formData, overrideDate: e.target.value})} 
                                                    className="w-full bg-[#08090b] border border-orange-900/50 text-orange-400 font-mono rounded-lg p-3 text-sm focus:border-orange-500 outline-none transition-colors" 
                                                />
                                                <p className="text-[9px] text-slate-500 mt-1">Überschreibt das Stamm-Datum für DIESEN Monat. Wird beim automatischen Rollover wieder gelöscht.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-3 block border-b border-slate-800 pb-2">Zielgruppe (Sichtbarkeit) *</label>
                                    <div className="flex flex-wrap gap-4">
                                        {EVENT_AUDIENCE_OPTIONS.map((opt) => (
                                            <label
                                                key={opt.key}
                                                className="flex items-center gap-2 cursor-pointer text-sm font-bold text-slate-300 hover:text-white transition-colors"
                                                title={opt.hint}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={formData.targetAudience?.includes(opt.key)}
                                                    onChange={() => handleAudienceToggle(opt.key)}
                                                    className="w-4 h-4 rounded border-slate-600 bg-[#08090b] text-[#E6C299] focus:ring-[#E6C299]"
                                                />
                                                {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* BLOCK 3: MONATS-INHALT */}
                                <div className="mt-8 pt-6 border-t border-slate-700">
                                    <h4 className="text-xs font-black text-white uppercase tracking-widest mb-4 flex items-center gap-2">
                                        <EditIcon size={14} className="text-[#E6C299]"/> Temporärer Monats-Inhalt
                                    </h4>
                                    
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">Aktuelles Thema</label>
                                            <input type="text" value={formData.currentTopic} onChange={e => setFormData({...formData, currentTopic: e.target.value})} className="w-full bg-[#08090b] border border-slate-700 text-white rounded-lg p-3 text-sm focus:border-[#E6C299] outline-none transition-colors" placeholder="z.B. Kalkulation im Sommergeschäft..." />
                                        </div>

                                        {/* FILE UPLOAD: Monats-Themenbild */}
                                        <div>
                                            <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block text-blue-400">Bild zum Thema (Extra-Eventbild für Mail & Events-Seite)</label>
                                            <div className="flex items-center gap-4">
                                                {(topicImageFile || formData.imageUrl) && (
                                                    <div className="w-16 h-16 rounded bg-[#08090b] border border-slate-700 overflow-hidden relative group">
                                                        <img src={topicImageFile ? URL.createObjectURL(topicImageFile) : formData.imageUrl} alt="Preview" className="w-full h-full object-cover" />
                                                        <button onClick={() => handleImageRemove('topic')} className="absolute inset-0 bg-red-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <TrashIcon size={16} />
                                                        </button>
                                                    </div>
                                                )}
                                                <div className="flex-1">
                                                    <input type="file" accept="image/*" onChange={(e) => { if(e.target.files && e.target.files[0]) setTopicImageFile(e.target.files[0]); }} className="hidden" id="upload-topic" />
                                                    <label htmlFor="upload-topic" className="flex items-center justify-center gap-2 w-full bg-slate-800 hover:bg-slate-700 border border-slate-600 border-dashed text-blue-300 rounded-lg p-3 text-sm font-bold uppercase tracking-widest cursor-pointer transition-colors">
                                                        <UploadCloudIcon size={18} /> {topicImageFile || formData.imageUrl ? 'Neues Themenbild wählen' : 'Bild hochladen'}
                                                    </label>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">Zusätzliche Beschreibung (Optional)</label>
                                            <p className="text-[10px] text-slate-500 mb-2">Nur Extra für den Monat. Der schöne Text steht oben unter Beschreibung und bleibt beim Monatswechsel. Formatierung wie im Newsletter (oben &amp; unten).</p>
                                            <div className="event-desc-quill-dark">
                                                <div id={descToolbarTopId} className="event-desc-quill-toolbar">
                                                    <span className="ql-formats">
                                                        <select className="ql-header" defaultValue="">
                                                            <option value="1" />
                                                            <option value="2" />
                                                            <option value="3" />
                                                            <option value="" />
                                                        </select>
                                                    </span>
                                                    <span className="ql-formats">
                                                        <button type="button" className="ql-bold" />
                                                        <button type="button" className="ql-italic" />
                                                        <button type="button" className="ql-underline" />
                                                        <button type="button" className="ql-strike" />
                                                    </span>
                                                    <span className="ql-formats">
                                                        <button type="button" className="ql-list" value="ordered" />
                                                        <button type="button" className="ql-list" value="bullet" />
                                                    </span>
                                                    <span className="ql-formats">
                                                        <button type="button" className="ql-link" />
                                                        <button type="button" className="ql-clean" />
                                                    </span>
                                                </div>
                                                <ReactQuill
                                                    ref={descQuillRef}
                                                    theme="snow"
                                                    value={formData.description || ''}
                                                    onChange={(html) =>
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            description: html,
                                                        }))
                                                    }
                                                    modules={descQuillModules}
                                                    placeholder="Weitere Infos zum Event..."
                                                />
                                                <div id={descToolbarBottomId} className="event-desc-quill-toolbar event-desc-quill-toolbar-bottom">
                                                    <span className="ql-formats">
                                                        <select className="ql-header" defaultValue="">
                                                            <option value="1" />
                                                            <option value="2" />
                                                            <option value="3" />
                                                            <option value="" />
                                                        </select>
                                                    </span>
                                                    <span className="ql-formats">
                                                        <button type="button" className="ql-bold" />
                                                        <button type="button" className="ql-italic" />
                                                        <button type="button" className="ql-underline" />
                                                        <button type="button" className="ql-strike" />
                                                    </span>
                                                    <span className="ql-formats">
                                                        <button type="button" className="ql-list" value="ordered" />
                                                        <button type="button" className="ql-list" value="bullet" />
                                                    </span>
                                                    <span className="ql-formats">
                                                        <button type="button" className="ql-link" />
                                                        <button type="button" className="ql-clean" />
                                                    </span>
                                                </div>
                                            </div>
                                            <style>{`
                                                .event-desc-quill-dark .event-desc-quill-toolbar {
                                                    border: 1px solid #334155;
                                                    background: #0f1015;
                                                    border-radius: 8px 8px 0 0;
                                                    padding: 6px 8px;
                                                }
                                                .event-desc-quill-dark .event-desc-quill-toolbar-bottom {
                                                    border-radius: 0 0 8px 8px;
                                                    border-top: none;
                                                    border: 1px solid #334155;
                                                    background: #0f1015;
                                                    padding: 6px 8px;
                                                }
                                                .event-desc-quill-dark .ql-container.ql-snow {
                                                    border: 1px solid #334155;
                                                    border-top: none;
                                                    border-bottom: none;
                                                    border-radius: 0;
                                                    color: #f8fafc;
                                                    font-size: 14px;
                                                    background: #08090b;
                                                    min-height: 110px;
                                                }
                                                .event-desc-quill-dark .ql-editor {
                                                    min-height: 110px;
                                                    color: #f8fafc;
                                                }
                                                .event-desc-quill-dark .ql-editor.ql-blank::before {
                                                    color: #475569;
                                                    font-style: normal;
                                                }
                                                .event-desc-quill-dark .ql-snow .ql-stroke { stroke: #cbd5e1; }
                                                .event-desc-quill-dark .ql-snow .ql-fill { fill: #cbd5e1; }
                                                .event-desc-quill-dark .ql-snow .ql-picker { color: #cbd5e1; }
                                                .event-desc-quill-dark .ql-snow .ql-picker-options { background: #0f1015; border-color: #334155; }
                                            `}</style>
                                        </div>

                                        <div>
                                            <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block">Aufzeichnung dieses Extra-Themas</label>
                                            <p className="text-[10px] text-slate-500 mb-2">Gewinnt vor der Einstellung oben. Nach dem Monatswechsel fällt es wieder auf den Serien-Standard zurück.</p>
                                            <div className="flex flex-wrap gap-5">
                                                <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-slate-300 hover:text-white">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.monthlyWillBeRecorded === true}
                                                        onChange={() =>
                                                            setFormData({
                                                                ...formData,
                                                                monthlyWillBeRecorded: formData.monthlyWillBeRecorded === true ? null : true,
                                                            })
                                                        }
                                                        className="w-4 h-4 rounded border-slate-600 bg-[#08090b] text-[#E6C299] focus:ring-[#E6C299]"
                                                    />
                                                    Wird aufgezeichnet
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer text-sm font-bold text-slate-300 hover:text-white">
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.monthlyWillBeRecorded === false}
                                                        onChange={() =>
                                                            setFormData({
                                                                ...formData,
                                                                monthlyWillBeRecorded: formData.monthlyWillBeRecorded === false ? null : false,
                                                            })
                                                        }
                                                        className="w-4 h-4 rounded border-slate-600 bg-[#08090b] text-[#E6C299] focus:ring-[#E6C299]"
                                                    />
                                                    Wird nicht aufgezeichnet
                                                </label>
                                            </div>
                                        </div>

                                        {editingId && (formData.currentTopic || formData.imageUrl || !isQuillDescriptionEmpty(formData.description || '')) && (
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    if (!window.confirm('Thema, Beschreibung und Themenbild jetzt löschen? Das Titelbild bleibt.')) return;
                                                    await clearMonthlyContent(editingId, formData.imageUrl);
                                                    setFormData(prev => ({ ...prev, currentTopic: '', description: '', imageUrl: '', monthlyContentBoundDate: '', monthlyWillBeRecorded: null }));
                                                    setTopicImageFile(null);
                                                }}
                                                className="text-[10px] font-bold uppercase tracking-widest text-red-400 hover:text-red-300 border border-red-900/50 bg-red-950/20 px-3 py-2 rounded-lg"
                                            >
                                                Monatsinhalt zurücksetzen
                                            </button>
                                        )}
                                    </div>
                                </div>
                                
                                {/* TEST-STATION & SPEICHER-BUTTONS */}
                                <div className="pt-6 border-t border-slate-800 space-y-4">
                                    <div className="bg-[#15171e] p-4 rounded-xl border border-slate-700 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                                        <div className="flex-1">
                                            <label className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2 block flex items-center gap-1">
                                                <FlaskIcon size={12} className="text-blue-400"/> Test-Erinnerung senden
                                            </label>
                                            <input 
                                                type="email" 
                                                value={testEmail} 
                                                onChange={e => setTestEmail(e.target.value)} 
                                                placeholder="deine@email.de" 
                                                className="w-full bg-[#08090b] border border-slate-700 text-white rounded-lg p-3 text-sm focus:border-blue-500 outline-none font-mono" 
                                            />
                                        </div>
                                        <button 
                                            onClick={handleTestReminder} 
                                            disabled={isTestSending || !testEmail} 
                                            className="w-full sm:w-auto bg-slate-800 hover:bg-slate-700 text-blue-400 border border-slate-700 px-6 py-3 rounded-lg text-xs font-bold uppercase tracking-widest transition-colors disabled:opacity-50"
                                        >
                                            {isTestSending ? 'Sendet...' : 'Test Senden'}
                                        </button>
                                    </div>

                                    <div className="flex gap-4">
                                        <button onClick={() => setView('list')} className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold uppercase tracking-widest text-xs py-4 rounded-xl transition-colors border border-slate-700">Abbrechen</button>
                                        <button onClick={handleSave} disabled={isLoading || isUploading || !formData.title || !formData.baseDate} className="flex-1 bg-[#E6C299] hover:bg-white text-black font-black uppercase tracking-widest text-xs py-4 rounded-xl transition-colors shadow-[0_0_15px_rgba(230,194,153,0.3)] disabled:opacity-50 disabled:cursor-not-allowed">
                                            {isUploading ? 'Lade Daten hoch...' : isLoading ? 'Speichert...' : 'Event Speichern'}
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EventAdminModal;